/**
 * Firebase Auth operations. Screens call these; session state is owned by
 * AppContext's onAuthStateChanged listener, so nothing here touches app state.
 */
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode,
  updateProfile,
  signOut,
} from 'firebase/auth';
import { auth } from '../firebase';

/** Human-readable messages for the Firebase Auth error codes we expect. */
const ERROR_MESSAGES = {
  'auth/email-already-in-use': 'An account with this email already exists. Try signing in instead.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-disabled': 'This account has been disabled. Please contact your administrator.',
  'auth/user-not-found': 'Invalid email or password.',
  'auth/wrong-password': 'Invalid email or password.',
  'auth/invalid-credential': 'Invalid email or password.',
  'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
  'auth/network-request-failed': 'Network error: cannot reach the authentication service. Check your connection and try again.',
  'auth/expired-action-code': 'This link has expired. Please request a new one.',
  'auth/invalid-action-code': 'This link is invalid or has already been used. Please request a new one.',
  'auth/weak-password': 'Password is too weak. Please choose a stronger password.',
};

export const authErrorMessage = (err, fallback) =>
  ERROR_MESSAGES[err?.code] || err?.message || fallback;

async function postAuthEmail(path, email) {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error || 'An error occurred. Please try again.');
  }
}

/**
 * Register a new account and send the verification email. The caller shows
 * a "check your email" message; the user stays signed out until verified
 * (AppContext ignores unverified sessions).
 */
export async function register(email, password, name) {
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  if (name) {
    await updateProfile(credential.user, { displayName: name });
  }
  await postAuthEmail('/api/auth/firebase/verify-email', email.toLowerCase().trim());
  return credential.user;
}

/**
 * Sign in. Resolves with { user, emailVerified }. When the email is not yet
 * verified the session is kept (so resendVerification works) but AppContext
 * will not treat the user as authenticated.
 */
export async function login(email, password) {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return { user: credential.user, emailVerified: credential.user.emailVerified };
}

/** Re-send the verification email for the currently signed-in user. */
export async function resendVerification() {
  if (!auth.currentUser?.email) {
    throw new Error('Please sign in first so we know which account to verify.');
  }
  await postAuthEmail(
    '/api/auth/firebase/verify-email',
    auth.currentUser.email.toLowerCase().trim()
  );
}

export async function requestPasswordReset(email) {
  await postAuthEmail('/api/auth/firebase/password-reset', email);
}

/** Validate a reset code from the email link; resolves with the account email. */
export async function verifyResetCode(oobCode) {
  return verifyPasswordResetCode(auth, oobCode);
}

export async function completePasswordReset(oobCode, newPassword) {
  await confirmPasswordReset(auth, oobCode, newPassword);
}

/** Apply an email-verification code from the email link. */
export async function completeEmailVerification(oobCode) {
  await applyActionCode(auth, oobCode);
  // Refresh the current session (if any) so emailVerified propagates.
  if (auth.currentUser) {
    await auth.currentUser.reload();
  }
}

export async function logout() {
  await signOut(auth);
}
